
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";

import {
  createVehicleAssignment,
  getVehicleAssignmentById,
  updateVehicleAssignment,
  uploadVehicleAssignmentDocument,
  getAvailableVehicles,
  getAvailableDrivers,
  getAvailableSuppliers,
  getAssignmentTypeOptions,   // NEW
} from "../../services/vehicleAssignment_service";

import { getDrivers } from "../../services/driver_service";
import { getVehicles } from "../../services/vehicle_service";
import {
  getSuppliers,
  getSupplierTypes,
  getServiceLocations,
  getBranches,
} from "../../services/supplier_service";

import { isAdmin, isSuperadmin, hasPermission } from "../../pages/utils/permissions";

import "./add_vehicleAssignments.css";

const OUTSTANDING_TYPE_NAMES = ["DCO", "VDP"];
const OWN_FLEET_NAME = "OWN FLEET";

const getLocalDateString = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};
const isImageUrl = (url) => !!url && /\.(jpg|jpeg|png|webp|gif)$/i.test(url);
const guessFileName = (url, fallbackLabel) => {
  const clean = url.split("?")[0].split("#")[0];
  const lastSegment = clean.split("/").pop() || "";
  const extMatch = lastSegment.match(/\.[a-zA-Z0-9]+$/);
  const ext = extMatch ? extMatch[0] : ".jpg";
  const base = (fallbackLabel || "document").replace(/[^\w\-]+/g, "_");
  return `${base}${ext}`;
};
const downloadFileAsBlob = async (url, label) => {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to fetch file (${res.status})`);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = guessFileName(url, label);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

export default function AddVehicleAssignment() {
  const navigate = useNavigate();
  const { id } = useParams();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const stageMode = !isEdit && searchParams.get("mode") === "stage";
  const isFreshCreate = !isEdit && !stageMode;
  const stageAssignmentTypeName = searchParams.get("assignment_type") || "";
  const isAdminUser = isAdmin();
  const isSuperadminUser = isSuperadmin();
  const canEdit =
    isSuperadminUser ||
    isAdminUser ||
    hasPermission("vehicle_assignments", "edit");
  const readOnly = isEdit && !canEdit;
  const identityLocked = stageMode;
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierTypes, setSupplierTypes] = useState([]);
  const [serviceLocations, setServiceLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [assignmentTypes, setAssignmentTypes] = useState([]); 
  const [supplierTypeFilter, setSupplierTypeFilter] = useState("");
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
  const [selectedVehicleLabel, setSelectedVehicleLabel] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showDriverSupplierDropdown, setShowDriverSupplierDropdown] = useState(false);
  const [driverSupplierSearch, setDriverSupplierSearch] = useState("");
  const driverSupplierRef = useRef(null);
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingFields, setUploadingFields] = useState({});
  const [downloadingFields, setDownloadingFields] = useState({});
  const [transactionNumber, setTransactionNumber] = useState("");
  const [chainId, setChainId] = useState("");
  const [parentUniqueNumber, setParentUniqueNumber] = useState("");
  const [isActive, setIsActive] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    service_location_id: "",
    branch_id: "",
    driver_id: "",
    vehicle_id: "",
    assignment_type_id: "",  
    supplier_id: "",
    vehicle_odometer_km: "",
    allotment_date: "",
    relieved_date: "",
    remarks: "",
    outstanding_amount: "",
    allotment_document_photo: "",
    vehicle_photo_front: "",
    vehicle_photo_back: "",
    vehicle_photo_left: "",
    vehicle_photo_right: "",
  });

  const stageAssignmentTypeObj = assignmentTypes.find(
    (t) => t.assignment_type_name?.toLowerCase() === stageAssignmentTypeName.toLowerCase()
  );
  const stageAssignmentTypeLabel = stageAssignmentTypeObj?.assignment_type_name || stageAssignmentTypeName;
  useEffect(() => { loadMasters(); }, []);
  useEffect(() => { if (isEdit) loadAssignment(); }, [id]);
  useEffect(() => {
    if (!stageMode) return;

    const qVehicleId = searchParams.get("vehicle_id");
    if (!qVehicleId) return;

    const qDriverId = searchParams.get("driver_id") || "";
    const qSupplierId = searchParams.get("supplier_id") || "";
    const qVehicleNo = searchParams.get("vehicle_no") || "";
    const qDisplayNo = searchParams.get("display_no") || "";
    const qLocationId = searchParams.get("service_location_id") || "";
    const qBranchId = searchParams.get("branch_id") || "";

    setFormData((prev) => ({
      ...prev,
      vehicle_id: qVehicleId,
      driver_id: qDriverId,
      supplier_id: qSupplierId,
      service_location_id: qLocationId,
      branch_id: qBranchId,
    }));

    setSelectedVehicleLabel(
      `${qVehicleNo}${qDisplayNo ? " - " + qDisplayNo : ""}`
    );
    setVehicleSearch(qVehicleNo);
  }, [stageMode]);
  useEffect(() => {
    if (!stageMode || !assignmentTypes.length) return;
    if (formData.assignment_type_id) return; // already resolved

    const match = assignmentTypes.find(
      (t) => t.assignment_type_name?.toLowerCase() === stageAssignmentTypeName.toLowerCase()
    );
    if (match) {
      setFormData((prev) => ({ ...prev, assignment_type_id: match.id }));
    }
 
  }, [stageMode, assignmentTypes]);
  useEffect(() => {
    if (!isFreshCreate || !assignmentTypes.length) return;
    if (formData.assignment_type_id) return; // already set

    const dispatchType = assignmentTypes.find(
      (t) => t.assignment_type_name?.toLowerCase() === "dispatch"
    );
    if (dispatchType) {
      setFormData((prev) => ({ ...prev, assignment_type_id: dispatchType.id }));
    }
  }, [isFreshCreate, assignmentTypes]);

  useEffect(() => {
    if (!stageMode) return;

    const qSupplierId = searchParams.get("supplier_id");
    const qDriverId = searchParams.get("driver_id");

    if (qSupplierId && suppliers.length) {
      const supplier = suppliers.find((s) => s.id === Number(qSupplierId));
      if (supplier) {
        setSelectedSupplier(supplier);
        if (supplier.supplier_type_id) {
          setSupplierTypeFilter(String(supplier.supplier_type_id));
          const supplierType = supplierTypes.find(
            (t) => t.id === supplier.supplier_type_id
          );
          setShowOutstanding(
            OUTSTANDING_TYPE_NAMES.includes(supplierType?.supplier_type_name ?? "")
          );
        }
      }
    } else if (qDriverId && drivers.length) {
      const matchedDriver = drivers.find((d) => String(d.id) === String(qDriverId));
      if (matchedDriver?.supplier_type_id) {
        setSupplierTypeFilter(String(matchedDriver.supplier_type_id));
      }
    }
  }, [stageMode, suppliers, drivers, supplierTypes]);

  useEffect(() => {
    if (vehicleSearch.length >= 4) {
      const filtered = vehicles.filter(
        (v) =>
          v.vehicle_registration_number?.toLowerCase().includes(vehicleSearch.toLowerCase()) ||
          v.vehicle_display_number?.toLowerCase().includes(vehicleSearch.toLowerCase())
      );
      setFilteredVehicles(filtered);
      setShowVehicleDropdown(true);
    } else {
      setFilteredVehicles([]);
      setShowVehicleDropdown(false);
    }
  }, [vehicleSearch, vehicles]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        driverSupplierRef.current &&
        !driverSupplierRef.current.contains(event.target)
      ) {
        setShowDriverSupplierDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const loadMasters = async () => {
    try {
      const useFullMasters = isEdit || stageMode;

      const [
        driverData,
        vehicleData,
        supplierData,
        supplierTypeData,
        locationData,
        branchData,
        assignmentTypeData,  
      ] = await Promise.all([
        useFullMasters ? getDrivers() : getAvailableDrivers(),
        useFullMasters ? getVehicles() : getAvailableVehicles(),
        useFullMasters ? getSuppliers() : getAvailableSuppliers(),
        getSupplierTypes(),
        getServiceLocations(),
        getBranches(),
        getAssignmentTypeOptions(),  
      ]);

      setDrivers(driverData || []);
      setVehicles(vehicleData || []);
      setSuppliers(supplierData || []);
      setSupplierTypes(supplierTypeData || []);
      setServiceLocations(locationData || []);
      setBranches(branchData || []);
      setAssignmentTypes(assignmentTypeData || []);   
    } catch (err) {
      console.error("Failed to load master data", err);
    }
  };

  const loadAssignment = async () => {
    try {
      const data = await getVehicleAssignmentById(id);

      setFormData({
        service_location_id: data.service_location_id ?? "",
        branch_id: data.branch_id ?? "",
        driver_id: data.driver_id ?? "",
        vehicle_id: data.vehicle_id ?? "",
        assignment_type_id: data.assignment_type_id ?? "", 
        supplier_id: data.supplier_id ?? "",
        vehicle_odometer_km: data.vehicle_odometer_km ?? "",
        allotment_date: data.assigned_date?.slice(0, 10) ?? "",
        relieved_date: data.relieved_date?.slice(0, 10) ?? "",
        remarks: data.remarks ?? "",
        outstanding_amount: data.outstanding_amount ?? "",
        allotment_document_photo: data.allotment_document_photo ?? "",
        vehicle_photo_front: data.vehicle_photo_front ?? "",
        vehicle_photo_back: data.vehicle_photo_back ?? "",
        vehicle_photo_left: data.vehicle_photo_left ?? "",
        vehicle_photo_right: data.vehicle_photo_right ?? "",
      });

      setTransactionNumber(data.unique_number || "");
      setChainId(data.chain_id || "");
      setParentUniqueNumber(data.parent_unique_number || "");
      setIsActive(data.is_active ?? null);

      if (data.vehicle_no) {
        setSelectedVehicleLabel(
          `${data.vehicle_no}${data.display_no ? " - " + data.display_no : ""}`
        );
        setVehicleSearch(data.vehicle_no);
      }

      if (data.supplier_id) {
        handleSupplierChange(data.supplier_id, supplierTypes, data.supplier_type_id);
      }

      if (data.supplier_type_id) {
        setSupplierTypeFilter(String(data.supplier_type_id));
      } else if (data.driver_id) {
        const matchedDriver = drivers.find((d) => d.id === data.driver_id);
        if (matchedDriver?.supplier_type_id) {
          setSupplierTypeFilter(String(matchedDriver.supplier_type_id));
        }
      }
    } catch (err) {
      console.error(err);
      alert("Failed to load assignment data");
    }
  };
  const handleChange = (e) => {
    if (readOnly) return;
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleVehicleSelect = (vehicle) => {
    if (readOnly || identityLocked) return;
    setFormData((prev) => ({ ...prev, vehicle_id: vehicle.id }));
    setSelectedVehicleLabel(
      `${vehicle.vehicle_registration_number}${vehicle.vehicle_display_number ? " - " + vehicle.vehicle_display_number : ""
      }`
    );
    setVehicleSearch(vehicle.vehicle_registration_number);
    setShowVehicleDropdown(false);
    if (errors.vehicle_id) setErrors((prev) => ({ ...prev, vehicle_id: undefined }));
  };

  const handleBack = async () => {
  if (readOnly) {
    navigate("/vehicle-assignment");
    return;
  }
  const confirmed = await confirm({
    title: "Unsaved Changes",
    message: "Are you sure you want to go back? Any unsaved changes will be lost.",
    confirmLabel: "Go Back",
    cancelLabel: "Stay on Page",
    danger: true,
    
  });
  if (confirmed) {
    navigate("/vehicle-assignment");
  }
};
  const selectedTypeName = (
    supplierTypes.find((t) => String(t.id) === String(supplierTypeFilter))
      ?.supplier_type_name || ""
  ).toUpperCase();
  const isOwnFleetSelected = selectedTypeName === OWN_FLEET_NAME;
  const filteredDrivers = isOwnFleetSelected
    ? drivers.filter((d) => String(d.supplier_type_id) === String(supplierTypeFilter))
    : drivers;
  const filteredSuppliers = supplierTypeFilter && !isOwnFleetSelected
    ? suppliers.filter((s) => String(s.supplier_type_id) === String(supplierTypeFilter))
    : suppliers;

  const searchText = driverSupplierSearch.trim().toLowerCase();

  const searchableDrivers = filteredDrivers.filter((d) => {
    if (!searchText) return true;

    return [
      d.driver_code,
      d.full_name,
      d.mobile,
      d.email,
    ].some((value) =>
      String(value ?? "").toLowerCase().includes(searchText)
    );
  });

  const searchableSuppliers = filteredSuppliers.filter((s) => {
    if (!searchText) return true;

    return [
      s.supplier_code,
      s.supplier_name,
      s.mobile,
      s.email,
    ].some((value) =>
      String(value ?? "").toLowerCase().includes(searchText)
    );
  });

  const selectedDriverObj = formData.driver_id
    ? drivers.find((d) => String(d.id) === String(formData.driver_id))
    : null;
  const selectedSupplierPhoto =
    selectedSupplier?.supplier_photo_url || selectedSupplier?.photo_url || null;
  const hasCapacityInfo =
    selectedSupplier?.max_vehicle_capacity !== undefined &&
    selectedSupplier?.remaining_capacity !== undefined;
  const handleSupplierTypeFilterChange = (e) => {
    if (readOnly || identityLocked) return;
    const value = e.target.value;
    setSupplierTypeFilter(value);
    setFormData((prev) => ({ ...prev, supplier_id: "", driver_id: "" }));
    setSelectedSupplier(null);
    setDriverSupplierSearch("");
    setShowDriverSupplierDropdown(false);
    setShowOutstanding(false);
    if (errors.driver_supplier_id) {
      setErrors((prev) => ({ ...prev, driver_supplier_id: undefined }));
    }
  };

  const handleSupplierChange = (supplierId, types = supplierTypes, typeId = null) => {
    if (readOnly || identityLocked) return;
    const supplier = suppliers.find((s) => s.id === Number(supplierId));
    setSelectedSupplier(supplier || null);

    if (supplier) {
      const supplierTypeId = typeId ?? supplier.supplier_type_id;
      const supplierType = types.find((t) => t.id === supplierTypeId);
      const typeName = supplierType?.supplier_type_name ?? "";
      setShowOutstanding(OUTSTANDING_TYPE_NAMES.includes(typeName));
    } else {
      setShowOutstanding(false);
    }

    setFormData((prev) => ({ ...prev, supplier_id: supplierId }));
    setDriverSupplierSearch(supplier?.supplier_name || "");
    setShowDriverSupplierDropdown(false);
    if (errors.driver_supplier_id) {
      setErrors((prev) => ({ ...prev, driver_supplier_id: undefined }));
    }
  };

  const handlePhotoChange = async (fieldName, e) => {
    if (readOnly) return;
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFields((prev) => ({ ...prev, [fieldName]: true }));
    try {
      const { path } = await uploadVehicleAssignmentDocument(fieldName, file);
      setFormData((prev) => ({ ...prev, [fieldName]: path }));
    } catch (err) {
      console.error(`Upload failed for ${fieldName}`, err);
      alert(`Failed to upload ${fieldName.replace(/_/g, " ")}`);
    } finally {
      setUploadingFields((prev) => ({ ...prev, [fieldName]: false }));
      e.target.value = "";
    }
  };

  const handleDownloadDoc = async (field, label) => {
    const url = formData[field];
    if (!url) return;
    setDownloadingFields((prev) => ({ ...prev, [field]: true }));
    try {
      await downloadFileAsBlob(url, label);
    } catch (err) {
      console.error(`Download failed for ${field}`, err);
      alert(`Failed to download ${label}`);
    } finally {
      setDownloadingFields((prev) => ({ ...prev, [field]: false }));
    }
  };
  const validate = () => {
    const e = {};

    if (!formData.service_location_id)
      e.service_location_id = "Please select a service location.";

    if (!formData.branch_id)
      e.branch_id = "Please select a branch.";
    if (!stageMode) {
      if (!formData.allotment_date) {
        e.allotment_date = "Allotment date is required.";
      } else if (formData.allotment_date > getLocalDateString()) {
        e.allotment_date = "Allotment date cannot be in the future.";
      }
    }
    if (stageMode) {
      if (!formData.relieved_date) {
        e.relieved_date = "Relieved date is required.";
      } else if (formData.relieved_date > getLocalDateString()) {
        e.relieved_date = "Relieved date cannot be in the future.";
      }
    }

    if (!formData.assignment_type_id)
      e.assignment_type_id = "Please select an allotment type.";

    if (!formData.vehicle_id)
      e.vehicle_id = "Please search for and select a vehicle.";
    if (formData.vehicle_odometer_km === "" || formData.vehicle_odometer_km === null) {
      e.vehicle_odometer_km = "Odometer reading is required.";
    } else if (String(formData.vehicle_odometer_km).trim() === "") {
      e.vehicle_odometer_km = "Odometer reading can't be just spaces.";
    } else if (isNaN(Number(formData.vehicle_odometer_km))) {
      e.vehicle_odometer_km = "Odometer reading must be a number.";
    } else if (Number(formData.vehicle_odometer_km) < 0) {
      e.vehicle_odometer_km = "Odometer reading can't be negative.";
    } else if (Number(formData.vehicle_odometer_km) >= 999999) {
      e.vehicle_odometer_km = "Odometer reading must be less than 9,99,999 KM.";
    }
    if (isOwnFleetSelected || !supplierTypeFilter) {
      if (!formData.driver_id)
        e.driver_supplier_id = "Please select a driver.";
    } else {
      if (!formData.supplier_id)
        e.driver_supplier_id = "Please select a supplier.";
    }
    if (showOutstanding && formData.outstanding_amount !== "") {
      if (String(formData.outstanding_amount).trim() === "") {
        e.outstanding_amount = "Outstanding amount can't be just spaces.";
      } else if (isNaN(Number(formData.outstanding_amount))) {
        e.outstanding_amount = "Outstanding amount must be a number.";
      } else if (Number(formData.outstanding_amount) < 0) {
        e.outstanding_amount = "Outstanding amount can't be negative.";
      }
    }
    if (formData.remarks && formData.remarks.trim() === "") {
      e.remarks = "Remarks can't be just spaces — leave it blank instead.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    if (!validate()) {
      const firstErr = document.querySelector(".field-error");
      if (firstErr) firstErr.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (Object.values(uploadingFields).some(Boolean)) {
      alert("Please wait for all uploads to finish before saving.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        driver_id: formData.driver_id ? Number(formData.driver_id) : null,
        vehicle_id: Number(formData.vehicle_id),
        supplier_id: formData.supplier_id ? Number(formData.supplier_id) : null,
        service_location_id: formData.service_location_id ? Number(formData.service_location_id) : null,
        branch_id: formData.branch_id ? Number(formData.branch_id) : null,
        vehicle_odometer_km: formData.vehicle_odometer_km ? Number(formData.vehicle_odometer_km) : null,
        assigned_date: stageMode ? (formData.relieved_date || null) : (formData.allotment_date || null),
        relieved_date: stageMode ? (formData.relieved_date || null) : null,
        assignment_type_id: Number(formData.assignment_type_id),
        remarks: formData.remarks || null,
        outstanding_amount: formData.outstanding_amount !== "" ? Number(formData.outstanding_amount) : null,
        allotment_document_photo: formData.allotment_document_photo || null,
        vehicle_photo_front: formData.vehicle_photo_front || null,
        vehicle_photo_back: formData.vehicle_photo_back || null,
        vehicle_photo_left: formData.vehicle_photo_left || null,
        vehicle_photo_right: formData.vehicle_photo_right || null,
      };

      if (isEdit) {
        await updateVehicleAssignment(id, payload);
        alert("Assignment updated successfully");
      } else {
        await createVehicleAssignment(payload);
        alert(
          stageMode
            ? `${stageAssignmentTypeLabel} recorded successfully`
            : "Vehicle assigned successfully"
        );
      }

      navigate("/vehicle-assignment");
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.detail || "Failed to save assignment");
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = isEdit
    ? readOnly ? "View Assignment" : "Edit Assignment"
    : stageMode
      ? `New ${stageAssignmentTypeLabel}`
      : "";
  const allotmentTypeOptions = isFreshCreate
    ? assignmentTypes.filter(
      (t) => t.assignment_type_name?.toLowerCase() === "dispatch"
    )
    : assignmentTypes;
  return (
    <div className="layout">
      <Sidebar />
      <div className="content">
        <Header />

        <div className="assignment-page">
          <div className="assignment-card">

            <div className="card-header">
              <h2>{pageTitle}</h2>
              {readOnly && (
                <span style={{
                  fontSize: 12, fontWeight: 700, color: "#0f766e",
                  background: "#ecfdf9", border: "1px solid #0d9488",
                  borderRadius: 999, padding: "3px 10px", marginLeft: 8,
                }}>
                  View Only
                </span>
              )}
            </div>

            {isEdit && (transactionNumber || chainId) && (
              <div style={{
                margin: "0 24px 16px", display: "flex", flexWrap: "wrap",
                gap: 8, alignItems: "center",
              }}>
                {transactionNumber && (
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: "#1e3a8a",
                    background: "#eff6ff", border: "1px solid #bfdbfe",
                    borderRadius: 999, padding: "4px 12px",
                  }} title="Auto-generated transaction number">
                    {transactionNumber}
                  </span>
                )}
                {chainId && (
                  <span style={{
                    fontSize: 12, fontWeight: 700, color: "#5b21b6",
                    background: "#f5f3ff", border: "1px solid #ddd6fe",
                    borderRadius: 999, padding: "4px 12px",
                  }} title="Groups every Dispatch/Handover/Recovery in this vehicle's lifecycle">
                    Chain: {chainId}
                  </span>
                )}
                {parentUniqueNumber && (
                  <span style={{
                    fontSize: 12, fontWeight: 600, color: "#6b7280",
                  }}>
                    ↳ from {parentUniqueNumber}
                  </span>
                )}
                {isActive !== null && (
                  <span style={{
                    fontSize: 12, fontWeight: 700,
                    color: isActive ? "#15803d" : "#6b7280",
                    background: isActive ? "#f0fdf4" : "#f3f4f6",
                    border: `1px solid ${isActive ? "#86efac" : "#d1d5db"}`,
                    borderRadius: 999, padding: "4px 12px",
                  }}>
                    {isActive ? "● Active" : "○ Closed"}
                  </span>
                )}
              </div>
            )}

            {stageMode && (
              <div style={{
                margin: "0 24px 16px", padding: "10px 14px",
                background: "#eff6ff", border: "1px solid #bfdbfe",
                borderRadius: 8, fontSize: 13, color: "#1e40af",
              }}>
                Recording a <strong>{stageAssignmentTypeLabel}</strong> for{" "}
                <strong>{selectedVehicleLabel || "this vehicle"}</strong>.
                Vehicle, driver/supplier, and type are locked to keep this
                linked to the original Dispatch — fill in the relieved date,
                odometer, remarks, and photos below.
              </div>
            )}



            <form onSubmit={handleSubmit}>
              <fieldset disabled={readOnly} style={{ border: "none", padding: 0, margin: 0 }}>
                <div className="form-section-title">Location Details</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Service Location <span className="req">*</span></label>
                    <select name="service_location_id" value={formData.service_location_id}
                      onChange={handleChange} required>
                      <option value="">Select Service Location</option>
                      {serviceLocations.map((loc) => (
                        <option key={loc.id} value={loc.id}>{loc.location_name}</option>
                      ))}
                    </select>
                    {errors.service_location_id && (
                      <span className="field-error">{errors.service_location_id}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Branch Name <span className="req">*</span></label>
                    <select name="branch_id" value={formData.branch_id}
                      onChange={handleChange} required>
                      <option value="">Select Branch</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                      ))}
                    </select>
                    {errors.branch_id && (
                      <span className="field-error">{errors.branch_id}</span>
                    )}
                  </div>
                  {!stageMode && (
                    <div className="form-group">
                      <label>Allotment Date</label>
                      <input type="date" name="allotment_date"
                        min="1900-01-01"
                        max={getLocalDateString()}
                        value={formData.allotment_date} onChange={handleChange} />
                      {errors.allotment_date && (
                        <span className="field-error">{errors.allotment_date}</span>
                      )}
                    </div>
                  )}
                  {stageMode && (
                    <div className="form-group">
                      <label>Relieved Date <span className="req">*</span></label>
                      <input
                        type="date"
                        name="relieved_date"
                        min="1900-01-01"
                        max={getLocalDateString()}
                        value={formData.relieved_date}
                        onChange={handleChange}
                      />
                      {errors.relieved_date && (
                        <span className="field-error">{errors.relieved_date}</span>
                      )}
                    </div>
                  )}

                  <div className="form-group">
                    <label>Allotment Type <span className="req">*</span></label>
                    <select name="assignment_type_id" value={formData.assignment_type_id}
                      onChange={handleChange} required disabled={identityLocked || isFreshCreate}>
                      <option value="">Select Type</option>
                      {allotmentTypeOptions.map((t) => (
                        <option key={t.id} value={t.id}>{t.assignment_type_name}</option>
                      ))}
                    </select>
                    {errors.assignment_type_id && (
                      <span className="field-error">{errors.assignment_type_id}</span>
                    )}
                  </div>

                </div>
                <div className="form-section-title">Vehicle Details</div>
                <div className="form-grid">

                  <div className="form-group" style={{ position: "relative" }}>
                    <label>Vehicle Number <span className="req">*</span></label>
                    <input
                      type="text"
                      placeholder="Type 4 digits of vehicle number..."
                      value={vehicleSearch}
                      disabled={identityLocked}
                      onChange={(e) => {
                        if (readOnly || identityLocked) return;
                        setVehicleSearch(e.target.value);
                        if (!e.target.value) {
                          setFormData((prev) => ({ ...prev, vehicle_id: "" }));
                          setSelectedVehicleLabel("");
                        }
                      }}
                      autoComplete="off"
                    />
                    {showVehicleDropdown && filteredVehicles.length > 0 && (
                      <div className="vehicle-dropdown">
                        {filteredVehicles.map((v) => (
                          <div key={v.id} className="vehicle-dropdown-item"
                            onClick={() => handleVehicleSelect(v)}>
                            <strong>{v.vehicle_registration_number}</strong>
                            {v.vehicle_display_number && (
                              <span> — {v.vehicle_display_number}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {showVehicleDropdown && filteredVehicles.length === 0 && (
                      <div className="vehicle-dropdown">
                        <div className="vehicle-dropdown-empty">No vehicles found</div>
                      </div>
                    )}
                    {selectedVehicleLabel && !showVehicleDropdown && (
                      <div className="selected-vehicle-label">✓ {selectedVehicleLabel}</div>
                    )}
                    {errors.vehicle_id && (
                      <span className="field-error">{errors.vehicle_id}</span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Vehicle Odometer (KM) <span className="req">*</span></label>
                    <input type="number" name="vehicle_odometer_km"
                      min="0"
                      max="999999"
                      required
                      value={formData.vehicle_odometer_km} onChange={handleChange}
                      placeholder="Enter current KM reading (max 9,99,999)"
                      className={errors.vehicle_odometer_km ? "input-error" : ""} />
                    {errors.vehicle_odometer_km && (
                      <span className="field-error">{errors.vehicle_odometer_km}</span>
                    )}
                  </div>

                </div>
                <div className="form-section-title">Supplier Type</div>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Supplier Type</label>
                    <select name="supplier_type_filter" value={supplierTypeFilter}
                      onChange={handleSupplierTypeFilterChange} disabled={identityLocked}>
                      <option value="">All Types</option>
                      {supplierTypes.map((t) => (
                        <option key={t.id} value={t.id}>{t.supplier_type_name}</option>
                      ))}
                    </select>
                    {isOwnFleetSelected && (
                      <span className="field-hint">
                        Own Fleet — the field below will show Own Fleet drivers.
                      </span>
                    )}
                  </div>
                </div>
                <div className="form-section-title">Driver / Supplier</div>
                <div className="form-grid">

                  <div
                    className="form-group"
                    ref={driverSupplierRef}
                    style={{ position: "relative" }}
                  >
                    <label>
                      Driver / Supplier{" "}
                      {isOwnFleetSelected && <span className="req">*</span>}
                    </label>

                    <div
                      className={`searchable-select ${
                        showDriverSupplierDropdown
                          ? "searchable-select-open"
                          : ""
                      }`}
                    >
                      <button
                        type="button"
                        className="searchable-select-control"
                        disabled={identityLocked}
                        onClick={() => {
                          if (readOnly || identityLocked) return;

                          setShowDriverSupplierDropdown((prev) => !prev);
                          setDriverSupplierSearch("");
                        }}
                      >
                        <span
                          className={
                            (
                              isOwnFleetSelected
                                ? formData.driver_id
                                : formData.supplier_id
                            )
                              ? "searchable-select-value"
                              : "searchable-select-placeholder"
                          }
                        >
                          {isOwnFleetSelected
                            ? (
                                drivers.find(
                                  (d) =>
                                    String(d.id) === String(formData.driver_id)
                                )?.full_name || "Select Driver"
                              )
                            : (
                                suppliers.find(
                                  (s) =>
                                    String(s.id) === String(formData.supplier_id)
                                )?.supplier_name || "Select Supplier"
                              )}
                        </span>

                        <span className="searchable-select-arrow">
                          {showDriverSupplierDropdown ? "▲" : "▼"}
                        </span>
                      </button>

                      {showDriverSupplierDropdown && (
                        <div className="searchable-select-dropdown">
                          <div className="searchable-select-search">
                            <input
                              type="text"
                              autoFocus
                              autoComplete="off"
                              value={driverSupplierSearch}
                              placeholder={
                                isOwnFleetSelected
                                  ? "Search driver by code, name or mobile..."
                                  : "Search supplier by code, name or mobile..."
                              }
                              onChange={(e) =>
                                setDriverSupplierSearch(e.target.value)
                              }
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          <div className="searchable-select-options">
                            {isOwnFleetSelected ? (
                              searchableDrivers.length > 0 ? (
                                searchableDrivers.map((d) => (
                                  <button
                                    type="button"
                                    key={d.id}
                                    className={`searchable-select-option ${
                                      String(formData.driver_id) === String(d.id)
                                        ? "selected"
                                        : ""
                                    }`}
                                    onClick={() => {
                                      if (readOnly || identityLocked) return;

                                      setFormData((prev) => ({
                                        ...prev,
                                        driver_id: d.id,
                                        supplier_id: "",
                                      }));

                                      setDriverSupplierSearch(
                                        d.full_name || ""
                                      );
                                      setShowDriverSupplierDropdown(false);

                                      if (errors.driver_supplier_id) {
                                        setErrors((prev) => ({
                                          ...prev,
                                          driver_supplier_id: undefined,
                                        }));
                                      }
                                    }}
                                  >
                                    <span className="searchable-option-main">
                                      {d.driver_code || "—"} — {d.full_name || "—"}
                                    </span>

                                    <span className="searchable-option-meta">
                                      {d.mobile || "No mobile"}
                                    </span>
                                  </button>
                                ))
                              ) : (
                                <div className="searchable-select-empty">
                                  No drivers found
                                </div>
                              )
                            ) : (
                              searchableSuppliers.length > 0 ? (
                                searchableSuppliers.map((s) => {
                                  const hasCap =
                                    s.remaining_capacity !== undefined &&
                                    s.max_vehicle_capacity !== undefined;

                                  const capSuffix = hasCap
                                    ? ` (${s.remaining_capacity}/${s.max_vehicle_capacity} slots left)`
                                    : "";

                                  return (
                                    <button
                                      type="button"
                                      key={s.id}
                                      className={`searchable-select-option ${
                                        String(formData.supplier_id) === String(s.id)
                                          ? "selected"
                                          : ""
                                      }`}
                                      onClick={() => {
                                        if (readOnly || identityLocked) return;

                                        handleSupplierChange(s.id);

                                        setDriverSupplierSearch(
                                          s.supplier_name || ""
                                        );
                                        setShowDriverSupplierDropdown(false);
                                      }}
                                    >
                                      <span className="searchable-option-main">
                                        {s.supplier_code
                                          ? `${s.supplier_code} — `
                                          : ""}
                                        {s.supplier_name || "—"}
                                        {capSuffix}
                                      </span>

                                      <span className="searchable-option-meta">
                                        {s.mobile || "No mobile"}
                                      </span>
                                    </button>
                                  );
                                })
                              ) : (
                                <div className="searchable-select-empty">
                                  No suppliers found
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {errors.driver_supplier_id && (
                      <span className="field-error">
                        {errors.driver_supplier_id}
                      </span>
                    )}

                    {isOwnFleetSelected && selectedDriverObj && (
                      <div className="selected-person-preview">
                        {selectedDriverObj.driver_photo_url ? (
                          <img
                            src={selectedDriverObj.driver_photo_url}
                            alt={selectedDriverObj.full_name}
                            className="selected-person-photo"
                          />
                        ) : (
                          <div className="selected-person-photo selected-person-photo--empty">
                            No Photo
                          </div>
                        )}
                        <div className="selected-person-info">
                          <strong>{selectedDriverObj.full_name}</strong>
                          <span>{selectedDriverObj.mobile}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedSupplier && !isOwnFleetSelected && (
                    <>
                      <div className="form-group">
                        <label>Photo</label>
                        {selectedSupplierPhoto ? (
                          <img
                            src={selectedSupplierPhoto}
                            alt={selectedSupplier.supplier_name}
                            className="selected-person-photo"
                          />
                        ) : (
                          <div className="selected-person-photo selected-person-photo--empty">
                            No Photo
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Code</label>
                        <input type="text" value={selectedSupplier.supplier_code || "—"}
                          readOnly className="readonly-input" />
                      </div>
                      <div className="form-group">
                        <label>Name</label>
                        <input type="text" value={selectedSupplier.supplier_name || "—"}
                          readOnly className="readonly-input" />
                      </div>
                      <div className="form-group">
                        <label>Contact No</label>
                        <input type="text" value={selectedSupplier.mobile || "—"}
                          readOnly className="readonly-input" />
                      </div>

                      {hasCapacityInfo && (
                        <div className="form-group">
                          <label>Vehicle Capacity</label>
                          <input
                            type="text"
                            value={`${selectedSupplier.current_vehicle_count ?? 0} / ${selectedSupplier.max_vehicle_capacity} used — ${selectedSupplier.remaining_capacity} slot${selectedSupplier.remaining_capacity === 1 ? "" : "s"} left`}
                            readOnly
                            className="readonly-input"
                          />
                        </div>
                      )}

                    </>
                  )}

                </div>
                <div className="form-section-title">Documents & Photos</div>
                <div className="form-grid">

                  {[
                    { field: "allotment_document_photo", label: "Allotment Document Photo", accept: "image/*,application/pdf" },
                    { field: "vehicle_photo_front", label: "Vehicle Photo — Front", accept: "image/*" },
                    { field: "vehicle_photo_back", label: "Vehicle Photo — Back", accept: "image/*" },
                    { field: "vehicle_photo_left", label: "Vehicle Photo — Left", accept: "image/*" },
                    { field: "vehicle_photo_right", label: "Vehicle Photo — Right", accept: "image/*" },
                  ].map(({ field, label, accept }) => (
                    <div className="form-group" key={field}>
                      <label>{label}</label>

                      {!readOnly && (
                        <input
                          type="file"
                          accept={accept}
                          onChange={(e) => handlePhotoChange(field, e)}
                          disabled={uploadingFields[field]}
                        />
                      )}

                      {uploadingFields[field] && (
                        <span className="file-uploading">Uploading…</span>
                      )}

                      {formData[field] && !uploadingFields[field] && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            marginTop: readOnly ? 0 : 6,
                          }}
                        >
                          {isImageUrl(formData[field]) && (
                            <img
                              src={formData[field]}
                              alt={label}
                              style={{
                                width: 48,
                                height: 48,
                                objectFit: "cover",
                                borderRadius: 6,
                                border: "1px solid #e5e7eb",
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <div style={{ display: "flex", gap: 10 }}>
                            <a
                              href={formData[field]}
                              target="_blank"
                              rel="noreferrer"
                              className="file-uploaded"
                            >
                              View
                            </a>

                            <a
                              href={formData[field]}
                              className="file-uploaded"
                              aria-disabled={downloadingFields[field]}
                              style={{
                                cursor: downloadingFields[field] ? "default" : "pointer",
                                opacity: downloadingFields[field] ? 0.6 : 1,
                                pointerEvents: downloadingFields[field] ? "none" : "auto",
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                if (downloadingFields[field]) return;
                                handleDownloadDoc(field, label);
                              }}
                            >
                              {downloadingFields[field] ? "Downloading…" : "Download"}
                            </a>
                          </div>
                        </div>
                      )}

                      {!formData[field] && !uploadingFields[field] && readOnly && (
                        <span className="cell-muted">Not uploaded</span>
                      )}
                    </div>
                  ))}

                </div>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Remarks</label>
                    <textarea rows="3" name="remarks" value={formData.remarks}
                      onChange={handleChange} placeholder="Enter remarks..." />
                    {errors.remarks && (
                      <span className="field-error">{errors.remarks}</span>
                    )}
                  </div>
                </div>

              </fieldset>
              <div className="btn-group">
                <button type="button" className="cancel-btn"
                  onClick={handleBack}>
                  {readOnly ? "Back" : "Cancel"}
                </button>
                {!readOnly && (
                  <button type="submit" className="save-btn" disabled={loading}>
                    {loading
                      ? "Saving..."
                      : isEdit
                        ? "Update Assignment"
                        : stageMode
                          ? `Save ${stageAssignmentTypeLabel}`
                          : "Save Allotment"}
                  </button>
                )}
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
}